"use client";
import GitHubStats from "@/components/ui/GitHubStats";
import { useAppContext } from "@/providers/AppProvider";
import { motion } from "framer-motion";
import { Code2, Database, Globe, Smartphone, User } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import GitHubCalendar from "react-github-calendar";
const Testimonials = dynamic(() => import("@/components/ui/Testimonials"), {
    ssr: false,
});

function Home() {
    const { mode, toggleMode } = useAppContext();

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex-1 p-2"
        >
            <div className="border-border mx-auto mb-4 flex max-w-min items-center justify-start gap-[3px] rounded-full border border-primary p-[3px] md:mx-0">
                <button
                    onClick={() => toggleMode()}
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                        mode === "tech"
                            ? "border border-primary bg-primary/15 text-primary shadow-sm"
                            : "text-text-color/80 hover:text-primary"
                    }`}
                >
                    <Code2 className="size-3.5" /> Technical
                </button>
                <button
                    onClick={() => toggleMode()}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                        mode === "simple"
                            ? "border border-primary bg-primary/15 text-primary shadow-sm"
                            : "text-text-color/80 hover:text-primary"
                    }`}
                >
                    <User className="size-3.5" /> Non-Technical
                </button>
            </div>
            {mode === "tech" ? <ForDevelopers /> : <ForVisitors />}
        </motion.div>
    );
}

export default Home;

function ForDevelopers() {
    const { user, theme } = useAppContext();
    const { about } = user || {};
    return (
        <>
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-base lg:text-lg"
            >
                {about?.split("\n").map((str, i) => (
                    <span key={i}>{str}</span>
                ))}
            </motion.p>
            <motion.hr
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className="mt-4 origin-left border-text-color"
            />
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="py-2 text-label-color"
            >
                Pro tip: Try my{" "}
                <Link href="/terminal" className="font-mono underline">
                    terminal mode
                </Link>{" "}
                for a different experience.
            </motion.p>
            <motion.hr
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className="mb-4 origin-left border-text-color"
            />
            {/* First section of about cards */}
            <section className="flex flex-wrap gap-4 pb-2">
                {[...Array(2)].map((_, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + i * 0.2 }}
                        className="about-card"
                    >
                        <Image
                            src={`/svgs/exp${i + 1}.svg`}
                            alt="Image"
                            width={0}
                            height={0}
                            sizes="100vw"
                            className="block h-full w-1/3 rounded-full object-contain"
                            priority
                            fetchPriority="high"
                        />
                        <div className="space-y-4">
                            <h3 className="text-xl font-medium">
                                {i === 0
                                    ? "Web Development"
                                    : "MERN Stack Development"}
                            </h3>
                            <p>
                                {i === 0
                                    ? "Build scalable web apps using the MERN stack from frontend to backend."
                                    : "Develop complete solutions with React, Node.js, Express, and MongoDB."}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </section>
            {/* Second section of about cards */}
            <section className="flex flex-wrap gap-4 py-2">
                {[...Array(2)].map((_, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 + i * 0.2 }}
                        className="about-card"
                    >
                        <Image
                            src={`/svgs/exp${i + 3}.svg`}
                            alt="Image"
                            width={0}
                            height={0}
                            sizes="100vw"
                            className="block h-full w-1/3 rounded-full object-contain"
                        />
                        <div className="space-y-4">
                            <h3 className="text-xl font-medium">
                                {i === 0
                                    ? "Mobile App Development"
                                    : "Team Leadership"}
                            </h3>
                            <p>
                                {i === 0
                                    ? "Build and review Flutter apps for mobile platforms."
                                    : "Lead a small dev team, review code, and ensure clean, scalable architecture."}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </section>
            <Testimonials />
            <GitHubStats />
            {/* GitHub Calendar Section */}
            <motion.section
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="w-[93dvw] pt-6 sm:w-[85dvw] md:w-[calc(100dvw-500px)]"
            >
                <GitHubCalendar
                    username="sahilatahar"
                    theme={{
                        dark: [
                            "rgb(30,30,31)",
                            "#383838",
                            "#4F4F4F",
                            "#777",
                            "#FFFFFF",
                        ],
                        light: [
                            "#ebedf0",
                            "#9be9a8",
                            "#40c463",
                            "#30a14f",
                            "#216e39",
                        ],
                    }}
                    colorScheme={theme}
                    hideTotalCount={false}
                    blockSize={16}
                />
            </motion.section>
        </>
    );
}
const services = [
    { icon: Globe, title: "Websites", subtitle: "Fast & modern web apps" },
    { icon: Smartphone, title: "Mobile apps", subtitle: "Android & iOS" },
    { icon: Database, title: "Systems", subtitle: "End to end backend" },
];

const stats = [
    { value: "2+", label: "Years experience" },
    { value: "3+", label: "Projects shipped" },
    { value: "2", label: "Platforms" },
];

function ForVisitors() {
    const { user } = useAppContext();

    return (
        <div className="flex min-h-[500px] items-center justify-center px-4 py-12">
            <div className="w-full max-w-2xl text-center">
                <motion.h2
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="mb-3 text-2xl font-medium"
                >
                    Hi, I&#39;m{" "}
                    <span className="text-primary">
                        {user?.name || "Sahil Atahar"}
                    </span>
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.4 }}
                    className="mx-auto max-w-xl text-base leading-relaxed text-text-color"
                >
                    I&#39;m a developer who builds websites, mobile apps, and
                    handles everything from the database to the screen. I enjoy
                    turning ideas into real products that people can actually
                    use, whether that&#39;s a business dashboard, a customer
                    platform, or an app on someone&#39;s phone. I care about
                    making things that work well, look good, and don&#39;t get
                    in the way.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, duration: 0.4 }}
                    className="my-6 flex items-center justify-center gap-8"
                >
                    {stats.map(({ value, label }) => (
                        <div key={label}>
                            <p className="text-2xl font-bold text-primary">
                                {value}
                            </p>
                            <p className="text-sm text-text-color">{label}</p>
                        </div>
                    ))}
                </motion.div>
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                    className="mx-auto mb-6 h-px w-24 origin-center bg-border-color"
                />
                <div className="grid grid-cols-3 gap-3">
                    {services.map(({ icon: Icon, title, subtitle }, i) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                delay: 0.35 + i * 0.08,
                                duration: 0.35,
                            }}
                            className="rounded-xl border border-border-color px-3 py-4"
                        >
                            <div className="mb-2 flex justify-center">
                                <Icon className="size-5 text-primary" />
                            </div>
                            <p className="text-sm font-medium">{title}</p>
                            <p className="text-sm text-text-color">
                                {subtitle}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}
